import { mapDateFromUI } from '@bridebook/toolbox';
import { mapDateToIsoFormat } from '@bridebook/toolbox/src/datepicker/map-date-to-from-ui';
import { IDatePickerUI } from '@bridebook/toolbox/src/datepicker/types';
import { IUISupplier } from '@bridebook/toolbox/src/types';
import { saveWeddingDate } from '@bridebook/web/lib/datepicker/actions';
import {
  bookedSupplierAnalytics,
  createCustomSupplier,
  saveToShortlist,
  toggleCustomSupplierFormModal,
} from '@bridebook/web/lib/shortlist/actions';
import {
  BookedMethod,
  IDeps,
  ShortlistedLocation,
  TUIShortlistSupplier,
  VenueConfirmSlides,
  VenueFoundAnswers,
} from '@bridebook/web/lib/types';
import { completedVenueSourceStepAnalytics } from '@bridebook/web/lib/venue-confirmation/analytics/actions';
import { VenueConfirmActionTypes } from './action-types';

export const triggerBookingConfirmationModal = ({
  supplier = null,
  showIntro,
  location,
  section,
  searchSupplierType,
}: {
  supplier?: IUISupplier | null;
  showIntro?: boolean;
  location: string;
  section?: string;
  searchSupplierType?: IUISupplier['type'];
}) => ({
  type: VenueConfirmActionTypes.TRIGGER_BOOKING_CONFIRMATION_MODAL,
  payload: { supplier, showIntro, location, searchSupplierType, section },
});

export const closeBookingConfirmationModal = () => ({
  type: VenueConfirmActionTypes.CLOSE_BOOKING_CONFIRMATION_MODAL,
});

export const venueBookedToggle = (value: VenueFoundAnswers) => ({
  type: VenueConfirmActionTypes.VENUE_BOOKED_TOGGLE,
  payload: value,
});

export const venueConfirmChangeSlide = (slideName: VenueConfirmSlides) => ({
  type: VenueConfirmActionTypes.CHANGE_SLIDE,
  payload: slideName,
});

export const venueConfirmSaveVenueInState = (venue: IUISupplier) => ({
  type: VenueConfirmActionTypes.SAVE_VENUE_TO_BOOK,
  payload: venue,
});

export const saveVenueEstimate = (price: number) => ({
  type: VenueConfirmActionTypes.SAVE_VENUE_PRICE,
  payload: price,
});

export const saveWeddingYear = (year: number) => ({
  type: VenueConfirmActionTypes.SAVE_WEDDING_YEAR,
  payload: year,
});

export const saveWeddingMonth = (month: number) => ({
  type: VenueConfirmActionTypes.SAVE_WEDDING_MONTH,
  payload: month,
});

export const triggerSupplierInterestConfirmation = () => ({
  type: VenueConfirmActionTypes.TRIGGER_SUPPLIER_CONFIRMATION,
});

export const saveSupplierIdToConfirm = (payload: string | null) => ({
  type: VenueConfirmActionTypes.SAVE_SUPPLIER_ID_TO_CONFIRM,
  payload,
});

export const confirmSupplierInterest = (payload: TUIShortlistSupplier) => ({
  type: VenueConfirmActionTypes.CONFIRM_SUPPLIER_INTEREST,
  payload,
});

export const denySupplierInterest = (payload: TUIShortlistSupplier) => ({
  type: VenueConfirmActionTypes.DENY_SUPPLIER_INTEREST,
  payload,
});

/**
 * Complete venue select step
 */
export const completeVenueSelectStep =
  (date: IDatePickerUI) =>
  ({ dispatch, getState }: IDeps) => {
    const { venueToBook, triggerLocation, triggerSection } = getState().venueConfirmation;

    if (venueToBook) {
      if (venueToBook.customSupplier) {
        dispatch(createCustomSupplier('confirmVenuePopup', venueToBook, true, 'shortlist', false));
        dispatch(toggleCustomSupplierFormModal(false, true, 'venueConfirmationPopup'));
      }

      /*
       https://bridebook.atlassian.net/browse/LIVE-21034 set shortlistedLocation to homeBookedSupplierWidget (not default to bookingConfirmationPopup) when booked via homeBookedSupplierWidget
      */
      const getShortlistedLocation: () => Extract<
        ShortlistedLocation,
        'homeBookedSupplierWidget' | 'bookingConfirmationPopup'
      > = () => {
        if (triggerLocation === 'homeBookedSupplierWidget') {
          return 'homeBookedSupplierWidget';
        }

        return 'bookingConfirmationPopup';
      };
      const shortlistedLocation = getShortlistedLocation();

      dispatch(
        saveToShortlist({
          supplier: { ...venueToBook, booked: true },
          method: 'confirmVenuePopup',
          shortlistedLocation,
          showSnackbar: false,
        }),
      );

      dispatch(
        bookedSupplierAnalytics({
          supplier: venueToBook,
          method: triggerLocation as BookedMethod,
          section: triggerSection,
        }),
      );
      if (date) {
        dispatch(saveWeddingDate(date));
      }
      const weddingDate = mapDateToIsoFormat(mapDateFromUI(date));
      dispatch(closeBookingConfirmationModal());

      return {
        type: VenueConfirmActionTypes.CONFIRM_VENUE,
        payload: { bookedVenue: venueToBook, weddingDate },
      };
    } else {
      return triggerSupplierInterestConfirmation();
    }
  };

/**
 * Complete venue source step
 */
export const completeVenueSourceStep =
  (discoverSource: string) =>
  ({ dispatch }: IDeps) => {
    // Analytics
    dispatch(completedVenueSourceStepAnalytics(discoverSource));

    return venueConfirmChangeSlide(VenueConfirmSlides.weddingMonth);
  };

/**
 * Complete datepicker step
 */
export const confirmVenue =
  (date: IDatePickerUI) =>
  ({ dispatch, getState }: IDeps) => {
    const bookedVenue = getState().venueConfirmation.venueToBook;
    if (!bookedVenue) throw Error('Confirm venue cannot be called without venue to book');

    if (date) {
      dispatch(saveWeddingDate(date));
    }

    const weddingDate = mapDateToIsoFormat(mapDateFromUI(date));

    dispatch(closeBookingConfirmationModal());
    return { type: VenueConfirmActionTypes.CONFIRM_VENUE, payload: { bookedVenue, weddingDate } };
  };
